// React
import React, { useState, useContext, useEffect, useCallback } from 'react'

// Kendo
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import {
    Chart,
    ChartLegend,
    ChartSeries,
    ChartSeriesItem,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartValueAxis,
    ChartValueAxisItem,
    ChartTooltip,
} from "@progress/kendo-react-charts";
import 'hammerjs';

// Global Context
import { GlobalContext } from '../../../contexts/Global';

// Components
import Title from '../../../components/Page/Title';
import Loader from '../../../components/Loader/Loader';

const AnalysisPage = () => {
    const { user } = useContext(GlobalContext);
    const [isLoading, showLoader] = useState(false);
    const [parkings, setParkings] = useState([]);
    const [selectedParking, setSelectedParking] = useState(null);
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d;
    });
    const [dateTo, setDateTo] = useState(new Date());
    const [chartData, setChartData] = useState(null);

    useEffect(() => {
        fetch(process.env.REACT_APP_API_BASE_URL + "/admin/parkings?has_snapshots=1", {
            headers: {
                Authorization: 'Bearer ' + user.access_token
            }
        })
            .then((response) => response.json())
            .then((response) => {
                setParkings(response.data.parkings);
            });
    }, [user]);

    const fetchAnalysis = useCallback(() => {
        if (!selectedParking) return;

        showLoader(true);

        fetch(process.env.REACT_APP_API_BASE_URL + "/admin/parkings/analysis", {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + user.access_token
            },
            body: JSON.stringify({
                parking_id: selectedParking.id,
                date_from: dateFrom.toISOString(),
                date_to: dateTo.toISOString()
            })
        })
            .then((response) => response.json())
            .then((response) => {
                showLoader(false);
                setChartData(response.data.levels);
            })
            .catch(() => {
                showLoader(false);
            });
    }, [selectedParking, dateFrom, dateTo, user]);

    useEffect(() => {
        fetchAnalysis();
    }, [fetchAnalysis]);

    const handleParkingChange = (e) => {
        setSelectedParking(e.target.value);
    };

    // Build chart categories and series from API data
    const buildChartData = () => {
        if (!chartData || chartData.length === 0) return null;

        // Collect all unique timestamps across all levels
        const timestampSet = new Set();
        chartData.forEach(level => {
            level.points.forEach(p => timestampSet.add(p.recorded_at));
        });
        const categories = Array.from(timestampSet).sort();

        // For each level, align data to categories as objects with value and date
        const series = chartData.map(level => {
            const dataMap = {};
            level.points.forEach(p => {
                dataMap[p.recorded_at] = p.free_spaces;
            });
            const data = categories.map(ts => ({
                value: dataMap[ts] !== undefined ? dataMap[ts] : null,
                date: ts
            }));
            return {
                name: 'Livello ' + level.level,
                data
            };
        });

        const formattedCategories = categories.map(ts => {
            const d = new Date(ts);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            return `${day}/${month} ${hours}:${minutes}`;
        });

        // Build alternating day bands
        const plotBands = [];
        let currentDay = null;
        let bandStart = 0;
        let dayIndex = 0;

        categories.forEach((ts, i) => {
            const day = new Date(ts).toDateString();
            if (day !== currentDay) {
                if (currentDay !== null) {
                    if (dayIndex % 2 === 0) {
                        plotBands.push({ from: bandStart, to: i, color: '#f5f5f5', opacity: 1 });
                    }
                    dayIndex++;
                }
                currentDay = day;
                bandStart = i;
            }
        });
        // Last day band
        if (dayIndex % 2 === 0) {
            plotBands.push({ from: bandStart, to: categories.length, color: '#f5f5f5', opacity: 1 });
        }

        return { categories: formattedCategories, series, plotBands };
    };

    const prepared = selectedParking ? buildChartData() : null;

    const formatDate = (ts) => {
        const d = new Date(ts);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    const tooltipRender = ({ point }) => {
        return `${formatDate(point.dataItem.date)} — ${point.series.name}: ${point.value}`;
    };

    return (
        <>
            <Title>Analisi delle statistiche dei parcheggi</Title>
            <div className="section">
                <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <div>
                        <label className="k-form-label" style={{ display: 'block', marginBottom: '4px' }}>Parcheggio</label>
                        <DropDownList
                            data={parkings}
                            textField="name"
                            dataItemKey="id"
                            value={selectedParking}
                            onChange={handleParkingChange}
                            style={{ width: '300px' }}
                            defaultItem={{ name: 'Seleziona un parcheggio', id: null }}
                        />
                    </div>
                    <div>
                        <label className="k-form-label" style={{ display: 'block', marginBottom: '4px' }}>Da</label>
                        <DatePicker
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.value)}
                            format="dd/MM/yyyy"
                        />
                    </div>
                    <div>
                        <label className="k-form-label" style={{ display: 'block', marginBottom: '4px' }}>A</label>
                        <DatePicker
                            value={dateTo}
                            onChange={(e) => setDateTo(e.value)}
                            format="dd/MM/yyyy"
                        />
                    </div>
                </div>

                {selectedParking && prepared && prepared.series.length > 0 && (
                    <Chart seriesColors={['#4CAF50', '#2E7D32']}>
                        <ChartLegend position="top" />
                        <ChartTooltip render={tooltipRender} />
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem
                                categories={prepared.categories}
                                labels={{
                                    rotation: -45,
                                    step: Math.max(1, Math.floor(prepared.categories.length / 20))
                                }}
                                majorGridLines={{
                                    step: Math.max(1, Math.floor(prepared.categories.length / 20)),
                                    color: '#e0e0e0',
                                    dashType: 'dash'
                                }}
                                plotBands={prepared.plotBands}
                            />
                        </ChartCategoryAxis>
                        <ChartValueAxis>
                            <ChartValueAxisItem
                                title={{ text: 'Posti liberi' }}
                                min={0}
                                majorGridLines={{ color: '#e0e0e0', dashType: 'dash' }}
                            />
                        </ChartValueAxis>
                        <ChartSeries>
                            {prepared.series.map((s, i) => (
                                <ChartSeriesItem
                                    key={i}
                                    type="line"
                                    name={s.name}
                                    data={s.data}
                                    field="value"
                                    missingValues="gap"
                                />
                            ))}
                        </ChartSeries>
                    </Chart>
                )}

                {selectedParking && chartData && chartData.length === 0 && (
                    <p style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                        Nessun dato disponibile per il periodo selezionato.
                    </p>
                )}
            </div>

            {isLoading && (<Loader />)}
        </>
    );
}

export default AnalysisPage;
